<template>
  <div class="embed">
    <section class="embed-head" aria-labelledby="embed-result-title">
      <score-ring :score="score" :grade="grade.label" :size="112" :stroke="10" />
      <div class="embed-summary">
        <span class="embed-grade" :class="`embed-grade--${grade.chip}`">{{ grade.label }}</span>
        <h1 id="embed-result-title" class="embed-headline">{{ grade.headline }}</h1>
        <p class="embed-line">
          {{ grade.line.before }}<strong v-if="grade.line.weak">{{ grade.line.weak }}</strong>{{ grade.line.after }}
        </p>
      </div>
    </section>

    <ul class="embed-chips" aria-label="Score per category">
      <li v-for="category in scoredCategories" :key="category.name" class="embed-chip">
        <span class="embed-chip-dot" :style="{ background: `var(--ex-score-${category.level})` }" aria-hidden="true" />
        {{ category.label }}
        <span class="embed-chip-score">{{ category.score }}</span>
      </li>
    </ul>

    <div class="embed-sections">
      <email-unlock
        v-if="!fullReport"
        compact
        :score="score"
        :check-count="checkCount"
        :uri="uri"
        :result-uri="emailResultUri"
        :public-json-uri="publicJsonUri"
      />
      <template v-else>
        <fixes-first v-if="fixes.length" :fixes="fixes" />
        <mobile-note />
        <all-checks
          :details="shownDetails"
          :counts="counts"
          :improvements-only="improvementsOnly"
          @toggle-improvements="$emit('toggle-improvements')"
        />
      </template>
    </div>

    <div class="embed-bar">
      <a :href="openUri" target="_blank" rel="noopener" class="ex-btn ex-btn--primary ex-btn--small">
        <span class="ex-btn__label" :data-label="openLabel">{{ openLabel }}</span>
        <ph-icon name="arrow-up-right" :size="16" />
      </a>
      <button v-if="inFrame" type="button" class="ex-btn ex-btn--secondary ex-btn--small" @click="rescore">
        <span class="ex-btn__label" data-label="Score again">Score again</span>
      </button>
    </div>
  </div>
</template>

<script>
import AllChecks from './AllChecks.vue'
import EmailUnlock from './EmailUnlock.vue'
import FixesFirst from './FixesFirst.vue'
import MobileNote from './MobileNote.vue'
import PhIcon from './PhIcon.vue'
import ScoreRing from './ScoreRing.vue'
import { formatScore, getCategoryLabel, getScoreLevel, isScoredCategory } from '../score/report.mjs'
import { EVENTS, trackEvent } from '../score/analytics'
import { EMBED_MESSAGES } from '../score/embed'

export default {
  name: 'EmbedResult',
  components: { AllChecks, EmailUnlock, FixesFirst, MobileNote, PhIcon, ScoreRing },
  props: {
    score: { type: [Number, String], required: true },
    details: { type: Object, required: true },
    shownDetails: { type: Object, required: true },
    grade: { type: Object, required: true },
    counts: { type: Object, required: true },
    fixes: { type: Array, required: true },
    fullReport: { type: Boolean, required: true },
    improvementsOnly: { type: Boolean, default: false },
    checkCount: { type: String, required: true },
    uri: { type: String, required: true },
    // The result on score.exatom.io, with the token when this is the full report.
    shareUri: { type: String, required: true },
    emailResultUri: { type: String, required: true },
    publicJsonUri: { type: String, required: true }
  },
  data () {
    return { inFrame: window.parent !== window, resizeObserver: null }
  },
  computed: {
    scoredCategories () {
      return Object.entries(this.details)
        .filter(([, category]) => isScoredCategory(category))
        .map(([name, category]) => ({
          name,
          label: getCategoryLabel(name),
          score: formatScore(category.score),
          level: getScoreLevel(category.score)
        }))
    },
    openUri () {
      const url = new URL(this.shareUri)
      url.searchParams.delete('embed')
      url.searchParams.delete('bg')
      return url.toString()
    },
    openLabel () {
      return this.fullReport ? 'Open full report' : 'Open on score.exatom.io'
    }
  },
  mounted () {
    trackEvent(EVENTS.extensionScored, { report: this.fullReport ? 'full report' : 'score only' })

    if (this.inFrame && 'ResizeObserver' in window) {
      // The app root, not the document: the document is never shorter than the frame, so it could not shrink.
      const app = this.$root.$el
      this.resizeObserver = new ResizeObserver(() => this.postToParent({
        type: EMBED_MESSAGES.resize,
        height: Math.ceil(app.getBoundingClientRect().height)
      }))
      this.resizeObserver.observe(app)
    }
  },
  beforeDestroy () {
    this.resizeObserver?.disconnect()
  },
  methods: {
    postToParent (message) {
      window.parent.postMessage(message, '*')
    },
    rescore () {
      this.postToParent({ type: EMBED_MESSAGES.rescore })
    }
  }
}
</script>

<style scoped>
.embed {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 4px 0;
}

.embed-head {
  display: flex;
  align-items: center;
  gap: 20px;
}

.embed-summary {
  min-width: 0;
}

.embed-grade {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: var(--ex-radius-pill);
  font-size: 12px;
  font-weight: 600;
}

.embed-grade--excellent {
  background: var(--ex-grade-excellent-bg);
  color: var(--ex-grade-excellent-ink);
}

.embed-grade--good {
  background: var(--ex-grade-good-bg);
  color: var(--ex-grade-good-ink);
}

.embed-grade--fair {
  background: var(--ex-grade-fair-bg);
  color: var(--ex-grade-fair-ink);
}

.embed-grade--poor {
  background: var(--ex-grade-poor-bg);
  color: var(--ex-grade-poor-ink);
}

.embed-headline {
  margin-top: 8px;
  font-size: 26px;
  letter-spacing: -0.03em;
}

.embed-line {
  margin-top: 6px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ex-color-neutral-700);
}

.embed-line strong {
  font-weight: 600;
}

.embed-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.embed-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-white);
  font-size: 13px;
  font-weight: 600;
}

.embed-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--ex-radius-pill);
}

.embed-chip-score {
  font-family: var(--ex-font-heading);
  color: var(--ex-color-neutral-700);
}

.embed-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Stays in view while the report scrolls inside the extension. */
.embed-bar {
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 8px;
  padding: 12px 0;
  background: linear-gradient(to bottom, transparent, var(--ex-color-white) 30%);
}

@media (max-width: 520px) {
  .embed-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
